import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, Input} from '@angular/core';
import { TitleCasePipe } from "@angular/common";
import { FormGroup, FormBuilder } from "@angular/forms";
import { Subscription, forkJoin, BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { DocumentManagementService } from "../../../../common/services/document-management.service";
import { OnBaseMappedKeyword, OnBaseKeywordMappingSearchSpec } from '../../../../common/models/onBaseIntegrationAndMapping.model';
import { WellSearchSpec, WellSearchResult } from "../../../../common/models/well.model";
import { WellService } from "../../../../common/services/well.service";
import { OrganizationWellsGridComponent } from "../../../../common/components/organization/components/organization-wells/organization-wells-grid.component";


@Component({
    selector: 'well-search-view',
    templateUrl: './well-search-view.component.html',
    styleUrls: ['./well-search-view.component.scss'],
    standalone: false
})
export class WellSearchViewComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() widgetTitle: string;


	@ViewChild(OrganizationWellsGridComponent, { static: true }) public _table: OrganizationWellsGridComponent;

    form: FormGroup;

    private countySubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    private townshipSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    countyObservable: Observable<string[]> = this.countySubject.asObservable();
    townshipObservable: Observable<string[]> = this.townshipSubject.asObservable();

    dropdownDefault: string = "--";
    disableSearch: boolean = true;
    searchActive: boolean = false;

    results: WellSearchResult[] = [];
	invalid: boolean = false;
	invalidAPI: boolean = false;
	invalidRICSNumber: boolean = false;


    subscription: Subscription = new Subscription();

    get apiNumber(): string {
        if (this.form == null) return "";
        const ctrl = this.form.get("apiNumber");
        if (ctrl == null) return "";
        return ctrl.value;
	}

	set apiNumber(value: string) {
		if (this.form != null) {
			const ctrl = this.form.get("apiNumber");
			if (ctrl != null) {
				ctrl.setValue(value);
			}
		}
	}

	get RBDMSOwnerNumber(): string {
		if (this.form == null) return "";
		const ctrl = this.form.get("RBDMSOwnerNumber");
		if (ctrl == null) return "";
		return ctrl.value;
	}

	set RBDMSOwnerNumber(value: string) {
		if (this.form != null) {
			const ctrl = this.form.get("RBDMSOwnerNumber");
			if (ctrl != null) {
				ctrl.setValue(value);
			}
		}
	}

	get RICSOrganizationId(): string {
		if (this.form == null) return "";
		const ctrl = this.form.get("RICSOrganizationId");
		if (ctrl == null) return "";
		return ctrl.value;
	}

	set RICSOrganizationId(value: string) {
		if (this.form != null) {
			const ctrl = this.form.get("RICSOrganizationId");
			if (ctrl != null) {
				ctrl.setValue(value);
			}	
		}
	}

    get selectedCounty(): string {
        if (this.form == null) return this.dropdownDefault;
        const ctrl = this.form.get("county");
        if (ctrl == null) return this.dropdownDefault;
        return ctrl.value;
	}

	set selectedCounty(value: string) {
		if (this.form != null) {
			const ctrl = this.form.get("county");
			if (ctrl != null) {
				ctrl.setValue(value);
			}
		}		
	}

    get selectedTownship(): string {
        if (this.form == null) return this.dropdownDefault;
        const ctrl = this.form.get("township");
        if (ctrl == null) return this.dropdownDefault;
        return ctrl.value;
	}

	set selectedTownship(value: string) {
		if (this.form != null) {
			const ctrl = this.form.get("township");
			if (ctrl != null) {
				ctrl.setValue(value);
			}
		}
	}


    constructor(private builder: FormBuilder, private casePipe: TitleCasePipe, private docService: DocumentManagementService, private wellService: WellService) {

    }

    ngOnInit() {
        this.countySubject.next([this.dropdownDefault]);
        this.townshipSubject.next([this.dropdownDefault]);
        this.form = this.builder.group({
            apiNumber: ["", []],
            county: [ this.dropdownDefault, []],
			township: [this.dropdownDefault, []],
			RICSOrganizationId: ["", []],
			RBDMSOwnerNumber: ["", []]
        });

        const county$ = this.docService.findKeywordMappings({ keywordName: "DOGRM - County" } as OnBaseKeywordMappingSearchSpec)
            .pipe(
                map((keys: OnBaseMappedKeyword[]) => {
                    let options: string[] = [];
                    keys.forEach(k => { k.dropdownValues.forEach(v => { options.push(v.value); }); });
                    options = options.filter(this.onlyUnique);
                    return options;
            })
        );

        const township$ = this.docService.findKeywordMappings({ keywordName: "DOGRM - Township" } as OnBaseKeywordMappingSearchSpec)
            .pipe(
                map((keys: OnBaseMappedKeyword[]) => {
                    let options: string[] = [];
                    keys.forEach(k => { k.dropdownValues.forEach(v => { options.push(this.casePipe.transform(v.value)); }); });
                    options = options.filter(this.onlyUnique);
                    return options;
                })
        );

        const dataObs$ = forkJoin(county$, township$).pipe()
            .subscribe(
                ([countyList, townshipList]) => {
                    countyList.unshift("--");
                    this.countySubject.next(countyList);
                    townshipList.unshift("--");
                    this.townshipSubject.next(townshipList);  
                    this.disableSearch = false;
                },
            (error) => { console.error(error); }
        );

        this.subscription.add(dataObs$);

        const change$ = this.form.valueChanges.pipe().subscribe(
            () => {
                this.invalid = false;
				this.invalidAPI = false;
				this.invalidRICSNumber = false;
                this.results = null;
				if (this._table != null) {
					this._table.setData(this.results);
				}
            },
            (error) => { console.error(error); }
        );
        this.subscription.add(change$);
        
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    ngAfterViewInit() {
		
    }

    export(): void {
		if (this._table != null) {
			this._table.exportWells();
		}
    }

    onlyUnique(value, index, self) {
        return self.indexOf(value) === index;
	}

	reset(): void {
		this.form.reset();
		this.apiNumber = "";
		this.RICSOrganizationId = "";
		this.RBDMSOwnerNumber = "";
		this.selectedTownship = this.dropdownDefault;
		this.selectedCounty = this.dropdownDefault;
		this.invalidAPI = false;
		this.invalidRICSNumber = false;
		this.invalid = false;
	}

	searchWells(): void {
		this.invalidAPI = this.apiNumber.length != 14;
		this.invalidRICSNumber = (this.RICSOrganizationId.length != 15 || this.RICSOrganizationId.startsWith("ON") == false) && this.RBDMSOwnerNumber.length == 0;
		this.invalid = this.invalidAPI && this.invalidRICSNumber && this.selectedCounty == this.dropdownDefault && this.selectedTownship == this.dropdownDefault;
		if (!this.invalid) {
			if (this.RICSOrganizationId.length != 15) { //resets the RICSOrganizationId field if it was only partially entered and another valid value was used
				this.RICSOrganizationId = "";
			}
			if (this.apiNumber.length != 14) {
				this.apiNumber = "";
			}
            const spec = {
                apiNumber: this.apiNumber,
                county: (this.selectedCounty != this.dropdownDefault) ? this.selectedCounty : "",
				township: (this.selectedTownship != this.dropdownDefault) ? this.selectedTownship : "",
				RBDMSOwnerNumber: this.RBDMSOwnerNumber,
				RICSOrganizationId: this.RICSOrganizationId
            } as WellSearchSpec;
            this.searchActive = true;
			if (this._table != null) {
				this._table.showLoading();
			}			
            const search$ = this.wellService.search(spec).pipe(                
            ).subscribe(
                (wells: WellSearchResult[]) => {
					this.results = wells;
					if (this._table != null) {
						this._table.setData(this.results);
					}
					this.searchActive = false;
                },
                (error) => {
                    console.error(error);
                    this.searchActive = false;
                }
            );
            this.subscription.add(search$);
        }
    }   
}
